import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 4.3 visual preview at /preview/lesson/4.3
 * "Notice what is specific: the seeing move"
 */

export default function LessonPreviewPage4_3() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 4 · Doing the work AI cannot do · Lesson 4.3
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Notice what is specific: the seeing move
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Voice · Judgment · Character</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 particular-replacement drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="Two openings. Same essay. Only one belongs to a person.">
      <p className="mt-6 text-text/90">
        Same student. Same college application. Same essay topic: a sport they
        love. Two opening lines, written by the same person on two different
        days.
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Opening A · The average sentence
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;I have always loved theater.&rdquo;
          </p>
          <p className="mt-4 text-text/80">
            True. Also written by roughly four million applicants this year. The
            reader has seen this opening twenty times today. Nothing in it tells
            the reader anything about this particular person on this particular
            stage.
          </p>
        </div>
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Opening B · The particular
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;The first time I forgot a line on opening night, I stared at
            the actor across from me and watched her decide to save us both.&rdquo;
          </p>
          <p className="mt-4 text-text/80">
            Also true. Nobody else can write this sentence because nobody else
            was that person at that moment. The reader sits up. The essay has
            already done its first job.
          </p>
        </div>
      </div>
      <p className="mt-6 text-text/90">
        Both sentences are about theater. Both are honest. The second one
        belongs to a person. The first one belongs to nobody.
      </p>
      <p className="mt-4 text-text/90">
        This is the move AI is worst at, because AI does not have particulars.
        AI has averages. The lesson today is how to see and use the particulars
        you already have and AI never will.
      </p>
    </Section>
  );
}

/* --- Section 2: From last time --- */
function FromLastTime() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Lesson 4.2" title="Through-line first. Particulars next.">
      <p className="mt-6 text-text/90">
        Three questions on yesterday. No looking back.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Define Mode collapse in one sentence." />
        <ReviewItem n="02" q="What is the cut 30% rule and why 30%?" />
        <ReviewItem n="03" q="Name the three signs a draft has no through-line." />
      </ol>
      <p className="mt-6 text-sm text-muted">
        Through-line gives the piece direction. Particulars give it weight. A
        clear through-line said in general terms is still flat. Today is how you
        weigh it down with the specifics only you have.
      </p>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The seeing move" title="AI generalizes. You particularize. Find what only you have.">
      <Subhead n={1} title="The vocab" />
      <p className="mt-4 text-text/90">
        There is a word for the detail that only somebody who was actually there
        could write. With the word, you can hunt for it. Without it, you settle
        for an average sentence and do not notice.
      </p>
      <VocabCard
        term="Particular"
        definition="A detail only available to someone who was actually there: a specific moment, a sentence somebody actually said, a number that surprised you, a small thing you noticed that nobody else would. Particulars are the opposite of generalities. They are how writing earns trust and how ideas earn original."
        analogy="A police sketch and a portrait both show a face. The sketch is general (round face, brown eyes, dark hair) and could be anyone. The portrait has one small mole, the way one eyebrow sits higher than the other, a chipped tooth in the smile. Only the portrait is a person. AI writes sketches. You can write portraits."
        memoryHook="If it could be true for ten thousand other people, it is not particular yet. If it could only be true for you on that day, it is."
        why="Particulars are where writing earns trust. A reader who reads a particular knows the writer was actually there. AI struggles here on purpose, because AI is trained on the average of what everyone has written. Your specific Tuesday afternoon is not in the average. Only you can put it on the page."
      />

      <Subhead n={2} title="Three places to mine for particulars" className="mt-12" />
      <p className="mt-4 text-text/90">
        Most students think they have nothing specific to say. They have plenty.
        They have not looked yet. These three places almost always produce one.
      </p>
      <MinesGrid />

      <Subhead n={3} title="The replacement move" className="mt-12" />
      <p className="mt-4 text-text/90">
        The drill is small and concrete. Find a flat general sentence in a draft.
        Replace it with one sentence containing a particular only you have. Do
        not add. Replace. One sentence in, one sentence out.
      </p>
      <ReplacementExample />

      <Subhead n={4} title="Why AI gets this wrong, every time" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is not lazy. AI is trained on text. Text on the internet is mostly
        generalities, because specifics belong to one person each. The model has
        read about being on a stage. The model has not been on one. That gap
        does not close with a better model. It closes when you write.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The test
        </p>
        <p className="mt-3 text-text/90">
          Read any sentence in your draft. Ask: could AI have written this with
          no facts from my life? If yes, that sentence is replaceable. Replace it
          with one that AI could not have written even if it tried.
        </p>
      </div>
    </Section>
  );
}

const MINES = [
  {
    n: '01',
    label: 'A moment',
    body: 'Not a season. Not an experience. One actual moment with a time and place. The half-second before something changed. The minute after a phone rang. AI cannot write a moment because AI was not in one.',
    example: 'Not: “Theater taught me confidence.” But: “The first time I forgot a line on opening night, I stared at the actor across from me and watched her decide to save us both.”',
  },
  {
    n: '02',
    label: 'A sentence somebody said',
    body: 'A line you actually heard, quoted as close to verbatim as you can. A coach, a parent, a friend, a stranger. Real quotes have rhythms AI cannot fake because real people do not talk in AI\'s rhythms.',
    example: 'Not: “My coach was tough but fair.” But: “After we lost the home opener my coach walked into the locker room and said, ‘you played scared.’ Then he left.”',
  },
  {
    n: '03',
    label: 'A number that surprised you',
    body: 'A specific count that you remember because it stuck. Not statistics from a search. Numbers you noticed in your actual life: how many, how much, how long, how fast. The reader trusts you more after one real number than after ten claims.',
    example: 'Not: “The shop got a lot of regulars.” But: “Forty-three regulars came in every week. I knew their orders without writing them down.”',
  },
];

function MinesGrid() {
  return (
    <div className="mt-6 space-y-4">
      {MINES.map((m) => (
        <div key={m.n} className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-xs text-muted">{m.n}</span>
            <h4 className="text-lg font-semibold text-emerald-300">{m.label}</h4>
          </div>
          <p
            className="mt-3 text-text/90"
            dangerouslySetInnerHTML={{ __html: m.body }}
          />
          <div className="mt-4 rounded-md border border-line bg-surface p-4">
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
              The shape
            </p>
            <p
              className="mt-2 text-sm text-text/90 italic"
              dangerouslySetInnerHTML={{ __html: m.example }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function ReplacementExample() {
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2">
      <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
          Before · Replaceable
        </p>
        <p className="mt-3 text-text/90 italic">
          &ldquo;Working at the deli taught me a lot about customer service and
          the value of hard work.&rdquo;
        </p>
        <p className="mt-3 text-sm text-text/80">
          Could be any deli, any worker, any summer. AI could write it with no
          information about you.
        </p>
      </div>
      <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          After · Particular
        </p>
        <p className="mt-3 text-text/90 italic">
          &ldquo;Every Tuesday at 4:15 a man named Frank came in for the same
          turkey sandwich. He never said hello. By August I had it ready before
          he reached the counter.&rdquo;
        </p>
        <p className="mt-3 text-sm text-text/80">
          Only you have Frank. Only you have 4:15. Only you have August. The
          paragraph that follows can now be about anything because the reader
          already trusts the writer was actually there.
        </p>
      </div>
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Replace the average sentence" title="Three flat sentences. Pick the best particular replacement.">
      <p className="mt-6 text-text/90">
        For each flat AI sentence, three rewrite candidates are offered. Pick the
        one that uses a real particular, not just more words. The other options
        are not wrong. They are still average.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <ParticularDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  flat: string;
  candidates: { key: string; text: string }[];
  correct: string;
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    flat: 'My family is very close.',
    candidates: [
      { key: 'a', text: 'My family has always been very close and supportive.' },
      { key: 'b', text: 'We share many traditions and values that bring us together.' },
      { key: 'c', text: 'Every Sunday at 6 PM my mom calls a number nobody answers anymore and leaves a message for my grandmother anyway.' },
    ],
    correct: 'c',
    verdict:
      'Option C. A and B are the same sentence in different clothes: both could be true for half the country. C names a specific time, a specific person, a specific act, and one fact about it that hurts. The reader knows things about this family after one sentence. The phrase “very close” never appears and does not need to.',
  },
  {
    n: '02',
    flat: 'I love to read.',
    candidates: [
      { key: 'a', text: 'I have always had a passion for reading and exploring new ideas.' },
      { key: 'b', text: 'I keep a stack of library books on my nightstand that is currently eleven books tall and tipping toward the wall.' },
      { key: 'c', text: 'Reading is one of my favorite hobbies and a way I unwind.' },
    ],
    correct: 'b',
    verdict:
      'Option B. A and C are the application-essay default and read like AI on a slow day. B shows a habit through one image with one number. The reader does not need to be told the writer loves to read. The reader can see it. Showing instead of stating is the whole point of the move.',
  },
  {
    n: '03',
    flat: 'My grandmother was a major influence on me.',
    candidates: [
      { key: 'a', text: 'My grandmother taught me many important life lessons that shaped who I am.' },
      { key: 'b', text: 'She was a kind and loving person who always made time for our family.' },
      { key: 'c', text: 'My grandmother kept a notebook of every recipe she ever cooked and the date she first made it. The earliest entry is March 1962.' },
    ],
    correct: 'c',
    verdict:
      'Option C. A and B are the kind of grandmother sentence every reader has read a hundred times: they are accurate about a great many grandmothers and specific about none of them. C names one habit, one object, one date. The reader now wants to read the next paragraph. The other two earned a polite nod and got skipped.',
  },
];

function ParticularDrill({ drill }: { drill: DrillSpec }) {
  // wasWrong wiring added below

  const [picked, setPicked] = useState<string | null>(null);
  const correctSentence = drill.candidates.find((s) => s.key === drill.correct);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The flat sentence
      </p>
      <p className="mt-3 text-text/90 italic">&ldquo;{drill.flat}&rdquo;</p>
      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Pick the rewrite that uses a real particular.
        </p>
        <div className="mt-3 space-y-2">
          {drill.candidates.map((s) => {
            const isPicked = picked === s.key;
            const isCorrect = s.key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'w-full rounded-md border border-line bg-elevated p-3 text-left text-sm text-text/85 hover:border-amber';
            if (shouldShowCorrect)
              cls =
                'w-full rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-3 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'w-full rounded-md border border-rose-500 bg-rose-500/[0.08] p-3 text-left text-sm text-text';
            return (
              <button
                key={s.key}
                className={cls}
                onClick={() => setPicked(s.key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {s.key.toUpperCase()}
                </span>
                <span className="mt-1 block">{s.text}</span>
              </button>
            );
          })}
        </div>
        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked?.toUpperCase()}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That rewrite is not the particular. See the correct answer below.
            </p>
          </div>
        )}
        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              Particular · Option {drill.correct.toUpperCase()}
            </p>
            <p className="mt-2 text-sm text-text/80 italic">
              &ldquo;{correctSentence?.text}&rdquo;
            </p>
            <p className="mt-3 text-sm text-text/90">{drill.verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define particular in one sentence." answer="A detail only available to someone who was actually there: a specific moment, a quoted line, a number that surprised you, a small thing only you would notice." />
        <QuickCheckItem n="Q2" q="Name the three places to mine for particulars." answer="A moment (with a time and place), a sentence somebody actually said, a number that surprised you." />
        <QuickCheckItem n="Q3" q="What is the one-sentence test for whether a sentence is particular?" answer="If it could be true for ten thousand other people, it is not particular yet. If it could only be true for you on that day, it is." />
        <QuickCheckItem n="Q4" q="Why is AI worst at this move, in one sentence?" answer="AI is trained on text on the internet, which is mostly generalities; the specifics of your Tuesday afternoon are not in the training data and a better model does not change that." />
        <QuickCheckItem n="Q5" q="The replacement move: state it." answer="Find a flat general sentence in your draft. Replace it with one sentence containing a particular only you have. One sentence in, one sentence out. Do not add length, change substance." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. Run it on every paragraph.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 4.3 · Lock card · Notice what is specific
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          AI generalizes. You particularize. Specifics are how writing earns trust
          and how a sentence proves you were actually there.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <MineTile n="01" name="A moment" body="One actual moment with a time and a place. The half-second before something changed." />
          <MineTile n="02" name="A line said" body="A real sentence somebody said, quoted close to verbatim. Real talk has rhythms AI cannot fake." />
          <MineTile n="03" name="A real number" body="A specific count from your life that stuck. One real number is worth ten general claims." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The test for any sentence
          </p>
          <p className="mt-2 text-text/90">
            Could AI have written this with no facts from my life? If yes, replace
            the sentence with one AI could not have written even if it tried. One
            sentence in, one sentence out. Do not add length.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          The reader trusts you after one real particular. After ten generalities
          they stop reading. Find the particular.
        </p>
      </div>
    </Section>
  );
}

function MineTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 4.4
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Defend what you shipped: the standing move
          </h3>
          <p className="mt-3 text-sm text-text/85">
            Vocab: Defensible. The defense kit: every claim to a source, every
            paragraph to a reason, every AI suggestion you took to a why. The
            three questions a teacher will ask and how to be ready for them.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 4.3 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p className="mt-3 border-t border-line pt-3 text-sm text-text/85">
          {answer}
        </p>
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    '4.2 review',
    'The seeing move',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
